import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { LoadingController, MenuController, AlertController } from '@ionic/angular';

import { Alert } from '../../services/alert';
import { UserService } from '../../services/user-service';
import { Util } from '../../services/util';
import { BusinessTasks } from '../../services/business-tasks';

import { MetaTagService } from 'src/app/services/meta-tag-service';
import { ObservableService } from 'src/app/services/observable-service';
import { Constants } from '../../models/constants';
import { NewProductDetails, ProductObj, ProductTasks } from '../../services/product-tasks';
import { PicFiles, PicService } from '../../services/pic-service';

@Component({
  selector: 'app-product-sales',
  templateUrl: './product-sales.page.html',
  styleUrls: ['./product-sales.page.scss'],
})
export class ProductSalesPage implements OnInit {

  productFields: {
    name?: string,
    sku?: string,
    listPriceString?: string,
    description?: string,
    otherDetails?: string,
    photoURL?: string,
  } = {}

  productsReady = false;
  activeProducts: ProductObj[];
  inactiveProducts: ProductObj[];
  addingProduct = false

  selectedTab = 'For Sale'

  commissionOptions: any[] = []

  loaded = false;

  formatPercentage = Util.formatPercentage
  formatCost = Util.formatCost

  constructor(
    private loadingCtrl: LoadingController,
    private alert: Alert,
    private observables: ObservableService,
    private changeRef: ChangeDetectorRef,
    public userService: UserService,
    private menuCtrl: MenuController,
    private metaTagService: MetaTagService,
    private alertCtrl: AlertController,
    private picService: PicService,
  ) {

    const business = this.userService.business

    if (!business) {
      const sub = this.observables.businessFetched.subscribe(() => {
        sub.unsubscribe()
        let eventualLoad = true
        this.loadUIContent(eventualLoad)
      });
      return
    }

    this.loadUIContent()
  }

  ngOnInit() {}

  loadUIContent(eventualLoad?: boolean) {

    this.loaded = true;
    this.commissionOptions = this.userService.getBusinessProductCommissionOptions()

    this.pullProducts();

    if (eventualLoad) {
      this.changeRef.detectChanges()
    }
  }

  async pullProducts() {

    const loading = await this.loadingCtrl.create();
    loading.present();

    ProductTasks.pullBusinessProducts((products: ProductObj[]) => {

      this.activeProducts = products.filter(p => p.active);
      this.inactiveProducts = products.filter(p => !p.active);
      this.productsReady = true;

      loading.dismiss()
      this.changeRef.detectChanges()

    }, (errorMsg: string) => {
      loading.dismiss()
      this.alert.show('Error', errorMsg)
    })
  }

  ionViewWillEnter() {
    this.metaTagService.updateTitle('Product Sales')
    this.menuCtrl.enable(true);
  }

  ionViewDidEnter() {
    this.observables.publishSetCurrentMenuRoot('/product-sales')
  }

  openPage(url: string) {
    window.open(url, '_blank')
  }

  openProSettings() {
    window.open(`${Constants.SCC_ADMIN_URL}/#/businessPros`, '_blank')
  }

  adminChat() {
    this.observables.publishOpenNewAdminChat();
  }


  selectTab(tab: string) {
    this.selectedTab = tab;
  }

  addProduct() {
    this.addingProduct = true;
  }
  closeNewProduct() {
    this.addingProduct = false;
  }
  async submitNewProduct() {
    if (!this.productFields.name) {
      this.error('Please enter product name.')
      return
    }
    if (!this.productFields.sku) {
      this.error('Please enter product SKU.')
      return
    }
    if (!this.productFields.description) {
      this.error('Please enter product description.')
      return
    }
    const listPrice = parseFloat(this.productFields.listPriceString);
    if (!listPrice) {
      this.error('Please enter list price as a number.')
      return
    }
    if (!this.productFields.photoURL) {
      this.error('Please add product image.')
      return
    }

    const newProduct: NewProductDetails = {
      name: this.productFields.name,
      sku: this.productFields.sku,
      description: this.productFields.description,
      otherDetails: this.productFields.otherDetails,
      listPrice: listPrice,
      photoURL: this.productFields.photoURL,
    }

    const loading = await this.loadingCtrl.create()
    loading.present()

    ProductTasks.addBusinessProduct(newProduct, () => {

      this.pullProducts();
      loading.dismiss();

    }, (err: string) => {

      loading.dismiss();
      this.error(err)
    })
  }
  async activateProduct(productID: string) {

    let c = confirm('Activate this product? It will be available for purchase immediately.');
    if (!c) return;

    const loading = await this.loadingCtrl.create()
    loading.present()

    ProductTasks.activateBusinessProduct(productID, () => {

      this.pullProducts();
      loading.dismiss();

    }, (err: string) => {

      loading.dismiss();
      this.error(err)
    })
  }
  async deactivateProduct(productID: string) {

    let c = confirm('Deactivate this product? It will be removed from your available catalog.');
    if (!c) return;

    const loading = await this.loadingCtrl.create()
    loading.present()

    ProductTasks.deactivateBusinessProduct(productID, () => {

      this.pullProducts();
      loading.dismiss();

    }, (err: string) => {

      loading.dismiss();
      this.error(err)
    })
  }
  addProductImage() {

    const shouldScale = false
    const shouldResize = false
    const showActionSheet = true

    this.picService.getPhoto(shouldScale, shouldResize, showActionSheet, async (files: PicFiles) => {

      const loading = await this.loadingCtrl.create({
        message: 'Uploading image'
      })
      loading.present()

      files.pic.save().then((savedFile: any) => {

        loading.dismiss()

        const photoURL = savedFile._url
        this.productFields.photoURL = photoURL
        this.changeRef.detectChanges()

      }, (errMsg: string) => {
        loading.dismiss()
        this.alert.show('Error', errMsg, 'Ok')
      });

    }, (errorMsg: string) => {
      this.alert.show('Error', errorMsg, 'Ok')
    })
  }



  async promptAddCommissionOption() {

    let alert = await this.alertCtrl.create({
      header: 'Add product commission tier for your Pros',
      inputs: [
        {
          name: 'name',
          placeholder: 'Name for this tier'
        },
        {
          name: 'commissionPercent',
          placeholder: 'Enter % as number (ie. 60)',
          type: 'number',
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Add option',
          handler: (data: any) => {
            let name = data.name
            let commission = parseInt(data.commissionPercent) / 100
            this.addCommissionOption(name, commission)
          }
        }
      ]
    });
    alert.present();
  }

  private async addCommissionOption(name: string, commission: number) {

    if (!name) {
      this.error('Invalid name.')
      return
    }
    if (!commission && commission !== 0) {
      this.error('Invalid commission.')
      return
    }

    let loading = await this.loadingCtrl.create()
    loading.present()

    BusinessTasks.addBusinessProductCommissionOption(name, commission, () => {

      this.userService.fetch(() => {

        this.commissionOptions = this.userService.getBusinessProductCommissionOptions()
        this.changeRef.detectChanges()

        loading.dismiss();

      }, (err: string) => {
        loading.dismiss();
        this.error(err)
      })

    }, (err: string) => {
      loading.dismiss();
      this.error(err)
    })
  }

  private error(msg: string) {
    this.alert.show("Error", msg);
  }

}
